import { useState, useEffect } from 'react';
import { vehiclesAPI, tenantUsersAPI } from '../../api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { FormLabel } from '../../components/ui/FormLabel';

const VehicleForm = ({ vehicle, onClose, onSuccess }) => {
  const isEdit = !!vehicle;

  const [tenantUsers, setTenantUsers] = useState([]);
  const [formData, setFormData] = useState({
    reg_number: vehicle?.reg_number || '',
    make: vehicle?.make || '',
    model: vehicle?.model || '',
    vin: vehicle?.vin || '',
    colour: vehicle?.colour || '',
    year: vehicle?.year || '',
    assigned_user_id: vehicle?.assigned_user_id || '',
    current_mileage: vehicle?.current_mileage ?? '',
    notes: vehicle?.notes || '',
    is_active: vehicle?.is_active !== undefined ? vehicle.is_active : true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await tenantUsersAPI.list();
        const users = response?.users || response || [];
        setTenantUsers(Array.isArray(users) ? users : []);
      } catch (err) {
        console.error('Failed to load tenant users:', err);
      }
    };
    loadUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        reg_number: formData.reg_number.trim(),
        make: formData.make?.trim() || null,
        model: formData.model?.trim() || null,
        vin: formData.vin?.trim() || null,
        colour: formData.colour?.trim() || null,
        year: formData.year ? parseInt(formData.year, 10) : null,
        assigned_user_id: formData.assigned_user_id || null,
        current_mileage: formData.current_mileage !== '' ? parseInt(formData.current_mileage, 10) : null,
        notes: formData.notes?.trim() || null,
        is_active: formData.is_active,
      };

      if (isEdit) {
        await vehiclesAPI.update(vehicle.id, payload);
      } else {
        await vehiclesAPI.create(payload);
      }
      onSuccess();
    } catch (err) {
      setError(
        err.message ||
          err.response?.data?.error ||
          err.response?.data?.errors?.[0]?.msg ||
          'Failed to save vehicle'
      );
      console.error('Form error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Registration */}
      <div>
        <FormLabel required>Registration Number</FormLabel>
        <Input
          type="text"
          name="reg_number"
          value={formData.reg_number}
          onChange={handleChange}
          required
          placeholder="AB12 CDE"
          className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Make & Model */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Make</FormLabel>
          <Input
            type="text"
            name="make"
            value={formData.make}
            onChange={handleChange}
            placeholder="Ford, Vauxhall"
            className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Model</FormLabel>
          <Input
            type="text"
            name="model"
            value={formData.model}
            onChange={handleChange}
            placeholder="Transit, Vivaro"
            className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {/* VIN, Colour, Year */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">VIN</FormLabel>
          <Input
            type="text"
            name="vin"
            value={formData.vin}
            onChange={handleChange}
            placeholder="Optional"
            className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Colour</FormLabel>
          <Input
            type="text"
            name="colour"
            value={formData.colour}
            onChange={handleChange}
            placeholder="e.g. Silver"
            className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Year</FormLabel>
          <Input
            type="number"
            name="year"
            value={formData.year}
            onChange={handleChange}
            min={1900}
            max={2100}
            placeholder="e.g. 2022"
            className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {/* Assigned User & Current Mileage */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Assigned to</FormLabel>
          <select
            name="assigned_user_id"
            value={formData.assigned_user_id}
            onChange={handleChange}
            className="flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— Unassigned —</option>
            {tenantUsers
              .filter((u) => ['tenant_admin', 'tenant_user'].includes(u.role))
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {[u.firstName ?? u.first_name, u.lastName ?? u.last_name].filter(Boolean).join(' ').trim() || 'Unknown'} {u.email ? `(${u.email})` : ''}
                </option>
              ))}
          </select>
        </div>
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Current Mileage</FormLabel>
          <Input
            type="number"
            name="current_mileage"
            value={formData.current_mileage}
            onChange={handleChange}
            min={0}
            placeholder="e.g. 45000"
            className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <FormLabel className="text-gray-700 dark:text-gray-300">Notes</FormLabel>
        <Textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          placeholder="Optional notes"
          className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Active (edit only) */}
      {isEdit && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <FormLabel htmlFor="is_active" className="mb-0 text-gray-700 dark:text-gray-300">
            Active
          </FormLabel>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-[var(--ds-border-divider)]">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? 'Saving...' : isEdit ? 'Update Vehicle' : 'Add Vehicle'}
        </Button>
      </div>
    </form>
  );
};

export default VehicleForm;
